import { PageController } from '@client/components/_core/layout/PageController';
import { Tabs } from '@client/components/_core/navigation/Tabs';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Skeleton } from 'antd';
import { match } from 'ts-pattern';
import { controlLibraryFormStore } from './store';

export const Route = createFileRoute('/admin-console/platform-content/obligations/controls-library/$id')({
    staticData: {
        breadcrumb: {
            title: 'Control details',
        },
        ui: {
            widthVariant: 'default',
        },
    },
    params: {
        parse({ id }) {
            return {
                id: BigInt(id),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ControlLibraryItemDetails />
        </PageController>
    );
}

function ControlLibraryItemDetails() {
    const { id } = Route.useParams();
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();
    const { mutateAsync: updateControlLibraryItem } = trpc.admin.controlLibrary.updateControlLibraryItem.useMutation();

    // Fetch control library item with tRPC query
    const controlQuery = trpc.admin.controlLibrary.getControlLibraryItem.useQuery(
        { id },
        {
            enabled: !!id,
            throwOnError(error) {
                message.destroy();

                match(error.data?.errorId)
                    .with('recordNotFound', () => {
                        void errorMessage
                            .showAsync("The control you are trying to access either doesn't exist.")
                            .then(() => {
                                navigate({ to: '/admin-console/platform-content/obligations/controls-library' });
                            });
                    })
                    .otherwise(() => {
                        errorMessage.show(error);
                    });

                return false;
            },
        },
    );

    const { formData: control } = controlLibraryFormStore.useInit({
        entity: 'control',
        data: controlQuery.data,
        isLoading: controlQuery.isLoading,

        async onReload() {
            await controlQuery.refetch();
        },

        async onSave(currentValue, newValue) {
            try {
                await updateControlLibraryItem({
                    id: currentValue.id!,
                    set: newValue,
                });

                await trpcUtils.admin.controlLibrary.invalidate();

                return { success: true };
            } catch (e) {
                return { success: false, error: e };
            }
        },
    });

    return control && id === control.id ? (
        <Tabs
            items={[
                {
                    label: 'Manage control',
                    to: '/admin-console/platform-content/obligations/controls-library/$id/details',
                },
                {
                    label: 'History',
                    to: '/admin-console/platform-content/obligations/controls-library/$id/history',
                },
            ]}
        />
    ) : (
        <Skeleton active />
    );
}
